import * as z from "zod";

// ─── Shared Zod schemas for the Recover 10DLC registrations dashboard ──────
//
// Lives in `redo/model` so both the server-side query helper
// (`redo/recover/db/util/src/list-recover-registrations.ts`) and the
// admin-sdk RPC schemas (`redo/admin/sdk/rpc/schema/phone-numbers/...`)
// can share one source of truth. Keeping the schemas here avoids a
// cross-package dependency from admin/sdk into redo/server and vice versa.

export const RegistrationStatusSchema = z.enum([
  "none",
  "pending",
  "verified",
  "rejected",
  "failed",
]);
export type RegistrationStatus = z.infer<typeof RegistrationStatusSchema>;

export const HubspotStageNormalizedSchema = z.enum([
  "unknown",
  "demo_set",
  "demo_set_no_show",
  "opportunity",
  "technical_evaluation",
  "proposal_negotiating",
  "onboarding_scheduled",
  "on_trial",
  "testing_ramping",
  "pending_approval",
  "closed_won",
  "closed_won_ramping",
  "under_contract",
  "failure_to_launch",
  "live",
  "onboarded",
  "closed_lost",
  "dollar_churn",
  "product_churn",
  "logo_churned",
  "win_back_opportunity",
  "churn_upsell",
  "product_win_back",
]);
export type HubspotStageNormalized = z.infer<
  typeof HubspotStageNormalizedSchema
>;

export const ProfileAttachStatusSchema = z.enum([
  "none",
  "partial",
  "attached",
]);
export type ProfileAttachStatus = z.infer<typeof ProfileAttachStatusSchema>;

export const UserRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
});

export const HubspotDealRefSchema = z.object({
  stage: HubspotStageNormalizedSchema.nullable(),
  stageLabel: z.string().nullable(),
  salesEngineer: z.string().nullable(),
  dealUrl: z.string().nullable(),
  updatedAt: z.date().nullable(),
});

const recoverRegistrationRowSchema_ = z.object({
  teamId: z.string(),
  teamName: z.string(),
  phoneNumber: z.string().nullable(),
  submittedAt: z.date().nullable(),
  submittedByUser: UserRefSchema.nullable(),
  profileStatus: z.enum(["none", "created"]),
  brandStatus: RegistrationStatusSchema,
  brandTelnyxId: z.string().nullable(),
  campaignStatus: RegistrationStatusSchema,
  campaignTelnyxId: z.string().nullable(),
  profileAttachStatus: ProfileAttachStatusSchema,
  hubspot: HubspotDealRefSchema.nullable(),
  reviewedDoneAt: z.date().nullable(),
  reviewedDoneByUser: UserRefSchema.nullable(),
});
export const RecoverRegistrationRowSchema: z.ZodType<RecoverRegistrationRow> =
  recoverRegistrationRowSchema_;
export interface RecoverRegistrationRow
  extends z.infer<typeof recoverRegistrationRowSchema_> {}

const listRecoverRegistrationsFilterSchema_ = z.object({
  hubspotStage: z.array(HubspotStageNormalizedSchema).optional(),
  brandStatus: z.array(RegistrationStatusSchema).optional(),
  campaignStatus: z.array(RegistrationStatusSchema).optional(),
  salesEngineer: z.string().optional(),
  reviewed: z.enum(["any", "done", "not-done"]).default("any"),
  search: z.string().optional(),
});
export const ListRecoverRegistrationsFilterSchema: z.ZodType<ListRecoverRegistrationsFilter> =
  listRecoverRegistrationsFilterSchema_;
export interface ListRecoverRegistrationsFilter
  extends z.infer<typeof listRecoverRegistrationsFilterSchema_> {}

const listRecoverRegistrationsSortSchema_ = z.object({
  field: z
    .enum(["submittedAt", "brandStatus", "campaignStatus"])
    .default("submittedAt"),
  direction: z.enum(["asc", "desc"]).default("desc"),
});
export const ListRecoverRegistrationsSortSchema: z.ZodType<ListRecoverRegistrationsSort> =
  listRecoverRegistrationsSortSchema_;
export interface ListRecoverRegistrationsSort
  extends z.infer<typeof listRecoverRegistrationsSortSchema_> {}

const listRecoverRegistrationsInputSchema_ = z.object({
  filter: listRecoverRegistrationsFilterSchema_.default({ reviewed: "any" }),
  sort: listRecoverRegistrationsSortSchema_.default({
    field: "submittedAt",
    direction: "desc",
  }),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
});
export const ListRecoverRegistrationsInputSchema: z.ZodType<ListRecoverRegistrationsInput> =
  listRecoverRegistrationsInputSchema_;
export interface ListRecoverRegistrationsInput
  extends z.infer<typeof listRecoverRegistrationsInputSchema_> {}

const listRecoverRegistrationsResultSchema_ = z.object({
  rows: z.array(RecoverRegistrationRowSchema),
  nextCursor: z.string().nullable(),
  counts: z.object({
    all: z.number().int(),
    // Zod v4's z.record(enum, …) is exhaustive and rejects responses
    // that omit any enum key. We only populate stages that actually
    // have registrations, so make it partial.
    byHubspotStage: z.partialRecord(
      HubspotStageNormalizedSchema,
      z.number().int(),
    ),
  }),
});
export const ListRecoverRegistrationsResultSchema: z.ZodType<ListRecoverRegistrationsResult> =
  listRecoverRegistrationsResultSchema_;
export interface ListRecoverRegistrationsResult
  extends z.infer<typeof listRecoverRegistrationsResultSchema_> {}
